<template>
  <section id="experience-section" class="common-padding mb-20">
    <div class="flex flex-col">
      <h3
        id="experienceHeading"
        v-html="experienceHeading"
        class="heading-1 text-start leading-none font-bold uppercase"
      ></h3>

      <div
        id="experience-text"
        class="md:column-gap text-flax-smoke-300 mt-[5%] grid grid-cols-12 justify-end opacity-0 lg:grid"
      >
        <p
          class="heading-6 text-flax-smoke-300/85 col-span-4 text-center text-nowrap lg:col-start-2"
        >
          ( WORK )
        </p>
        <p
          class="heading-4 font-fancy col-span-8 w-full text-balance sm:font-semibold lg:col-span-7"
        >
          Enterprise products I've built as a Software Developer, spanning
          web, mobile and AI-assisted tooling.
        </p>
      </div>
    </div>

    <div class="mt-12 lg:mt-[10%]">
      <div
        class="border-flax-smoke-300 flex flex-col justify-between gap-2 border-b pb-6 sm:flex-row sm:items-end"
      >
        <div>
          <h4 class="heading-3 font-title! font-bold uppercase">
            Arfitect Information Technologies
          </h4>
          <p class="heading-6 text-flax-smoke-400 mt-1">Software Developer</p>
        </div>
        <p class="heading-6 text-flax-smoke-400 text-nowrap">
          08/2021 — 08/2026 · İzmir, Türkiye
        </p>
      </div>

      <div class="mt-10 grid grid-cols-1 gap-8 md:grid-cols-2">
        <div
          v-for="project in companyProjects"
          :key="project.name"
          class="exp-card border-flax-smoke-300/50 rounded-lg border p-6"
        >
          <h5 class="heading-5 font-title! font-bold uppercase">
            {{ project.name }}
          </h5>
          <p class="text-flax-smoke-300/80 mt-3 text-base font-medium">
            {{ project.description }}
          </p>
        </div>
      </div>
    </div>

    <div class="mt-20">
      <div
        class="border-flax-smoke-300 flex flex-col justify-between gap-2 border-b pb-6 sm:flex-row sm:items-end"
      >
        <h4 class="heading-3 font-title! font-bold uppercase">Skills</h4>
        <p
          class="heading-6 text-flax-smoke-400 max-w-[40ch] text-balance sm:text-end"
        >
          Technologies and tools I use across backend, frontend, mobile, and
          delivery.
        </p>
      </div>

      <div class="mt-10 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
        <div
          v-for="(group, gi) in skillGroups"
          :key="group.label"
          class="exp-card border-flax-smoke-300/50 rounded-lg border p-6"
        >
          <div class="flex items-center justify-between gap-3">
            <h5 class="heading-6 font-title! font-bold uppercase">
              {{ group.label }}
            </h5>
            <span class="text-flax-smoke-500 font-mono text-sm">{{
              String(gi + 1).padStart(2, '0')
            }}</span>
          </div>
          <div class="mt-4 flex flex-wrap gap-2">
            <span
              v-for="skill in group.items"
              :key="skill"
              class="border-flax-smoke-300 hover:bg-flax-smoke-300 hover:text-flax-smoke-900 rounded-full border px-3 py-1.5 text-sm transition-[background-color,color] duration-300 ease-in-out"
            >
              {{ skill }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <div class="border-flax-smoke-300/50 mt-20 grid grid-cols-1 gap-8 border-t pt-10 sm:grid-cols-2">
      <div v-for="edu in education" :key="edu.degree">
        <p class="heading-6 text-flax-smoke-400 uppercase">{{ edu.period }}</p>
        <h5 class="heading-5 font-title! mt-1 font-bold">{{ edu.degree }}</h5>
        <p class="text-flax-smoke-300/80 mt-1">{{ edu.school }}</p>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
  import { animateSplitText } from '@/animations';
  import { textSplitterIntoChar } from '@/functions';
  import { onBeforeMount, onMounted, ref } from 'vue';
  import gsap from 'gsap';

  const experienceHeading = ref('Experience /');

  const companyProjects = [
    {
      name: 'Socar OT Hub',
      description:
        'Enterprise mobile hub for a petrochemical company consolidating field risk assessment, change management, and daily operations, with enterprise iOS distribution.',
    },
    {
      name: 'Osigo',
      description:
        'Digital insurance platform connecting brokers, insurers, and clients to manage custom insurance products, reducing manual processing time by 60% for 500+ daily active users.',
    },
    {
      name: 'Borçelik Root Cause Analysis',
      description:
        'White-label app for manufacturing engineers to investigate quality issues via Fishbone and 5 Whys workflows and AI-assisted querying of production data.',
    },
    {
      name: 'Tüpraş Digital Employee Portal',
      description:
        'Admin panel with React interfaces for a social-media-style content feed, smart search, daily announcements, and chatbot integrations, serving 10,000+ employees.',
    },
    {
      name: 'ConnectMind AI',
      description:
        'Mobile MVP of an AI focus group research platform simulating discussions with AI personas to help entrepreneurs validate ideas.',
    },
    {
      name: 'Naos Stars & Naos Portal',
      description:
        'Loyalty and e-commerce app for a cosmetics group (QR-based points, gift redemption, pharmacy locator), and its companion B2B app for pharmacy staff.',
    },
    {
      name: 'SoCo',
      description:
        'AI voice-coaching app with real-time, bidirectional voice conversations and a custom low-latency, echo-cancelled audio module.',
    },
    {
      name: 'SOCAR ESD & SOCAR MOC',
      description:
        'Enterprise systems digitizing the approval and monitoring lifecycle for safety-bypass overrides and industrial process changes at refinery facilities.',
    },
    {
      name: 'SunExpress Extra',
      description:
        'Certification exam-booklet management system for airline technical training covering EASA Part-66 question banks — used by 200+ pilots and technicians, cutting assessment cycle time by 50%.',
    },
  ];

  const skillGroups = [
    {
      label: 'APIs & Backend',
      items: [
        '.NET Core',
        'EF Core',
        'ABP Framework',
        'WorkflowCore',
        'Hangfire',
        'MinIO',
        'Node.js',
        'REST API',
        'Python',
        'GraphQL',
        'Firebase',
        'Supabase',
        'PostgreSQL',
        'SQL Server',
      ],
    },
    {
      label: 'Web Frontend',
      items: [
        'TypeScript',
        'JavaScript (ES6+)',
        'React.js',
        'Next.js',
        'HTML5',
        'CSS3',
        'Tailwind CSS',
      ],
    },
    {
      label: 'Mobile',
      items: [
        'Flutter',
        'Dart',
        'Bloc',
        'Riverpod',
        'MobX',
        'Dio',
        'go_router',
        'get_it',
        'React Native',
        'Expo',
        'NativewindUI',
      ],
    },
    {
      label: 'DevOps & Architecture',
      items: [
        'Git',
        'CI/CD',
        'Azure DevOps',
        'Bitbucket Pipelines',
        'Docker',
        'Clean Architecture',
        'Unit & Integration Testing',
      ],
    },
    {
      label: 'Tools',
      items: ['Cursor', 'Claude Code', 'ChatGPT', 'Figma'],
    },
  ];

  const education = [
    {
      degree: 'Master of Software Engineering',
      school: 'İzmir Katip Çelebi University',
      period: '2023 — Present',
    },
    {
      degree: 'Bachelor of Computer Engineering',
      school: 'Çukurova University',
      period: '2016 — 2021',
    },
  ];

  onBeforeMount(() => {
    experienceHeading.value = textSplitterIntoChar('Experience / ', true);
  });

  onMounted(() => {
    animateSplitText(
      '#experienceHeading .letters',
      '#experience-text',
      0.7,
      0.01,
      0,
    );

    gsap.utils.toArray('.exp-card').forEach((card: any) => {
      gsap.fromTo(
        card,
        { opacity: 0, y: 24 },
        {
          opacity: 1,
          y: 0,
          duration: 0.6,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: card,
            start: 'top 90%',
            toggleActions: 'play none none reverse',
          },
        },
      );
    });
  });
</script>
